import React, { ChangeEvent, useEffect, useState } from 'react'

import Modal from 'components/Modal'
import styled from 'styled-components'

import { color } from 'styles/colors'
import { AdminNotificationsPolicy } from 'types/AdminNotificationsPolicy'

import Api from 'services/api'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import TextInput from 'components/TextInput'
import Select, { SelectOption } from 'components/Select'
import RadioInputs from 'components/RadioInputs'

import { ButtonContainer } from 'styles/shared'
import { useConfirmDialog } from 'hooks/useDialog'
import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

type Props = {
    code: string
    alert?: Required<AdminNotificationsPolicy>
    onCloseCallBack: () => void
  }
const INTERVAL: SelectOption[] = [
  { name: 'minutes', value: 'minutes' },
  { name: 'seconds', value: 'seconds' },
  { name: 'hours', value: 'hours' }
]
const NotificationsModel = ({ onCloseCallBack, code, alert } : Props) => {
  const [selectedInputMessage, setSelectedInputMessage] = useState(0)
  const [isSelectedInputMessage, setIsSelectedInputMessage] = useState(false)
  const [occurrenceCount, setOccuranceCount] = useState<number | null>(null)
  const [occurrenceTime, setOccurrenceTime] = useState<number | null>(null)
  const [occurrenceInterval, setOccurrenceInterval] = useState(INTERVAL[0].value)
  const [activeCount, setActiveCount] = useState<number | null>(null)
  const [activeInterval, setActiveInterval] = useState(INTERVAL[0].value)
  const [isSaving, setIsSaving] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { confirm } = useConfirmDialog()

  const Inputs = [
    { value: 0, backgroundColor: color('paneBackground'), label: 'Send' },
    { value: 1, backgroundColor: color('paneBackground'), label: "Don't send" }
  ]

  const OccuranceInputs = [
    { value: 2, backgroundColor: color('paneBackground'), label: 'Occurs' }
  ]

  const ActiveAlertsInput = [
    { value: 3, backgroundColor: color('paneBackground'), label: 'Is active (not cleared) for more than' }
  ]

  useEffect(() => {
    if (alert?.enabled) {
      if (alert.activeCount) {
        setSelectedInputMessage(3)
        setActiveCount(alert.activeCount)
        setActiveInterval(alert.activeInterval)
      } else if (alert.occurrenceCount || alert.occurrenceTime) {
        setSelectedInputMessage(2)
        setOccuranceCount(alert.occurrenceCount)
        setOccurrenceTime(alert.occurrenceTime)
        setOccurrenceInterval(alert.occurrenceInterval)
      } else {
        setSelectedInputMessage(0)
      }
    } else {
      setSelectedInputMessage(1)
    }
  }, [alert])

  const onChangeSelectedInput = (value:number) => {
    setErrorMessage('')
    setSelectedInputMessage(Number(value))
    setIsSelectedInputMessage(true)
    if (value === 2) {
      setActiveCount(null)
    } else if (value === 3) {
      setOccuranceCount(null)
      setOccurrenceTime(null)
    } else {
      setActiveCount(null)
      setOccuranceCount(null)
      setOccurrenceTime(null)
    }
  }

  const onChangeOccurance = (event: ChangeEvent<HTMLInputElement>) => {
    setOccuranceCount(event.target.value.replace(/\D/g, '') ? Number(event.target.value.replace(/\D/g, '')) : null)
    onChangeSelectedInput(2)
  }

  const onChangeOccuranceInterval = (event: ChangeEvent<HTMLInputElement>) => {
    setOccurrenceTime(event.target.value.replace(/\D/g, '') ? Number(event.target.value.replace(/\D/g, '')) : null)
    onChangeSelectedInput(2)
  }

  const onChangeActiveInterval = (event: ChangeEvent<HTMLInputElement>) => {
    setActiveCount(event.target.value.replace(/\D/g, '') ? Number(event.target.value.replace(/\D/g, '')) : null)
    onChangeSelectedInput(3)
  }

  const onCancel = async () => {
    if (isSelectedInputMessage) {
      const isConfirmed = await confirm({
        title: CONFIRMATION_DIALOG_MESSAGES.CANCEL.TITLE,
        description: CONFIRMATION_DIALOG_MESSAGES.CANCEL.DESCRIPTION,
        confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.OK
      })
      if (isConfirmed) {
        onCloseCallBack()
      }
    } else {
      onCloseCallBack()
    }
  }

  const updatePolicy = (updatedAlertPolicy: Partial<AdminNotificationsPolicy>) => {
    Api.AlertNotifications.update(updatedAlertPolicy, code)
    setErrorMessage('')
    onCloseCallBack()
  }

  const onSave = () => {
    setIsSaving(true)
    const enabled = selectedInputMessage !== 1
    let updatedAlertPolicy : Partial<AdminNotificationsPolicy> = {}
    if (selectedInputMessage === 3 && activeCount) {
      updatedAlertPolicy = { ...updatedAlertPolicy, ...{ activeCount, activeInterval, enabled } }
      updatePolicy(updatedAlertPolicy)
    } else if (selectedInputMessage === 2 && (occurrenceCount || occurrenceTime)) {
      updatedAlertPolicy = { ...updatedAlertPolicy, ...{ occurrenceCount, occurrenceTime, occurrenceInterval, enabled } }
      updatePolicy(updatedAlertPolicy)
    } else if (selectedInputMessage === 0 || selectedInputMessage === 1) {
      updatedAlertPolicy = { ...updatedAlertPolicy, ...{ enabled } }
      updatePolicy(updatedAlertPolicy)
    } else {
      setErrorMessage('Enter required fields')
      setIsSaving(false)
    }
  }

  return (
    <Modal isDisplayed closeCallback={onCloseCallBack} variant="sm" >
      <S.H3>POLICY FOR:<S.Code>{code}</S.Code></S.H3>
      <S.RadioInputs selectedValue={selectedInputMessage} radioOptions={Inputs} onChange={(event) => onChangeSelectedInput(Number(event.target.value))} />
      <div>SEND IF</div>
      <S.RadioContainer>
        <S.RadioInputs selectedValue={selectedInputMessage} radioOptions={OccuranceInputs} onChange={(event) => onChangeSelectedInput(Number(event.target.value))} />
        <S.TextInput disabled={selectedInputMessage !== 2} type="text" errorMessage={selectedInputMessage === 2 ? errorMessage : ''} value={occurrenceCount?.toString() ?? ''} onChange={(event) => onChangeOccurance(event)} />
        <S.Span> times in</S.Span>
        <S.TextInput type="text" disabled={selectedInputMessage !== 2} errorMessage={selectedInputMessage === 2 ? errorMessage : ''}value={occurrenceTime?.toString() ?? ''} onChange={(event) => onChangeOccuranceInterval(event)} />
        <S.Select label="" disabled={selectedInputMessage !== 2} selectOptions={[INTERVAL]} value={occurrenceInterval} onChange={(event) => setOccurrenceInterval(event.target.value)} />
      </S.RadioContainer>
      <S.RadioContainer>
        <S.RadioInputs selectedValue={selectedInputMessage} radioOptions={ActiveAlertsInput} onChange={(event) => onChangeSelectedInput(Number(event.target.value))} />
        <S.TextInput type="text" disabled={selectedInputMessage !== 3} errorMessage={selectedInputMessage === 3 ? errorMessage : ''} value={activeCount?.toString() ?? ''} onChange={(event) => onChangeActiveInterval(event)} />
        <S.Select disabled={selectedInputMessage !== 3} label="" selectOptions={[INTERVAL]} value={activeInterval} onChange={(event) => setActiveInterval(event.target.value)} />
      </S.RadioContainer>
      <S.ButtonContainer>
        <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={onCancel} />
        <Button decoration="positive" enabled={!isSaving} text="Save" onPress={onSave} />
      </S.ButtonContainer>
    </Modal>
  )
}

export default NotificationsModel

const S = {
  Code: styled.span`
    padding-left: 12px;
  `,
  Span: styled.span`
    margin-top: 10px;
  `,
  ButtonContainer: styled(ButtonContainer)`
    margin-top: 10px;
  `,
  H3: styled.h3`
    color: ${color('thirdHeaderText')};
  `,
  TextInput: styled(TextInput)`
    width: 100px;
    margin-left: 10px;
    margin-right: 10px;
    -webkit-appearance: none !important;
    -moz-appearance: textfield !important;
  `,
  RadioContainer: styled.div`
    display: inline-flex;
    margin-top:10px;
  `,
  Select: styled(Select)`
    display: inline-flex;
    padding-left: 17px;
  `,
  RadioInputs: styled(RadioInputs)`
    flex-direction: column;

    & > :nth-child(n+1) {
      padding: 5px 10px 5px 10px;
      margin: 5px 0px 5px 0px;

      & > label:first-of-type {
        border: none;
        text-align: left;
        font-weight: inherit;
        text-transform: inherit;
        font-size: inherit;
        margin: 0px;
        padding: 0px 0px 0px 10px;
      }
    }
  `
}
